import React, { useState, useEffect } from "react";
import { Button } from "semantic-ui-react";
import { useAuth } from "../contexts/AuthContext";
import AnalyticsSidebar from "../components/analyticsSidebar";
import StudioData from "../components/studioData";
import "charts.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangle } from "@fortawesome/pro-solid-svg-icons"; // プロ版のアイコンをインポート

function AnalyticsPage() {
  const { logout } = useAuth();
  const currentYear = new Date().getFullYear();
  const YEAR_RANGE = 5; // 最新年から過去N年分を表示
  const years = Array.from({ length: YEAR_RANGE }, (_, i) => currentYear - i);
  // ログデータ取得
  const [trades, setTrades] = useState([]);
  const [monthPayment, setMonthPayment] = useState([]); // 売上の数値
  const [studios, setStudios] = useState([]); // スタジオリスト
  const [selectedYear, setSelectedYear] = useState(
    String(currentYear)
  ); // 選択した年
  const [selectedStudio, setSelectedStudio] = useState(null); // 選択したスタジオ
  const [selectedTrials, setSelectedTrials] = useState([0]); // 選択した体験店舗
  const [trialsCount, setTrialsCount] = useState([]);

  // ローカルでの状態管理
  const [localStudioCosts, setLocalStudioCosts] = useState([]);
  const [localAdvertisingCosts, setLocalAdvertisingCosts] = useState([]);
  const [localTrainfares, setLocalTrainfares] = useState([]);
  const [isFocus, setIsFocus] = useState([]);
  const [isMembersCountOpen, setIsMembersCountOpen] = useState(false);
  const [isRevenueOpen, setIsRevenueOpen] = useState(false);
  const [isSelectedTab, setIsSelectedTab] = useState(0); // 0: 全スタジオ合計, 1: スタジオ別比較
  const [revenues, setRevenues] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [studioAnalyticsData, setStudioAnalyticsData] = useState([]);
  const [isAllStudioUpdating, setIsAllStudioUpdating] = useState(false);
  const [isMonthPaymentLoading, setIsMonthPaymentLoading] = useState(false);
  const [isTrialsLoading, setIsTrialsLoading] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({
    done: 0,
    total: 0,
    percent: 0,
    year: "",
    studio_name: "",
    month: null,
  });

  const planPrices = {
    week1_plan: 5610,
    week2_plan: 10120,
    week3_plan: 15576,
    week4_plan: 17600,
    week5_plan: 22000,
    week6_plan: 26400,
    other_plan: 5610,
  };

  useEffect(() => {
    // 売上の合計
    const calculateTotalRevenueAndSalary = (monthPayment) => {
      return monthPayment.map((payment, index) => {
        const totalRevenue = Object.entries(payment.plan_students).reduce(
          (total, [plan, count]) => {
            const planRevenue =
              plan === "other_plan"
                ? planPrices["other_plan"] * count
                : (planPrices[plan] /
                    parseInt(plan.replace("week", "").replace("_plan", ""))) *
                  count;
            return total + planRevenue;
          },
          0
        );

        const totalSalary = Object.entries(payment.plan_students).reduce(
          (total, [plan, count]) => {
            let planSalary;
            if (plan === "other_plan" || plan === "week1_plan") {
              planSalary = 1360 * count;
            } else if (plan === "week2_plan") {
              planSalary = 1000 * count;
            } else {
              planSalary = 900 * count;
            }
            return total + planSalary;
          },
          0
        );

        return { month: index + 1, revenue: totalRevenue, salary: totalSalary };
      });
    };

    // 給料の合計
    const totalRevenuesAndSalaries =
      calculateTotalRevenueAndSalary(monthPayment);
    setRevenues(
      totalRevenuesAndSalaries.map((item) => ({
        month: item.month,
        revenue: item.revenue,
      }))
    );
    setSalaries(
      totalRevenuesAndSalaries.map((item) => ({
        month: item.month,
        salary: item.salary,
      }))
    );
  }, [monthPayment]);

  // 統計データの取得
  const fetchMonthPayment = async () => {
    if (selectedStudio === null) return;

    setIsMonthPaymentLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_month_payment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          year: selectedYear,
          studio_id: selectedStudio.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const analytics = await response.json();
      selectedStudio.id === 0
        ? setMonthPayment(aggregateMonthlyData(analytics))
        : setMonthPayment(analytics);
      selectedStudio.id === 0
        ? setStudioAnalyticsData(analytics)
        : setStudioAnalyticsData([
            {
              studio_id: selectedStudio.id,
              studio_name: selectedStudio.name,
              prefecture: selectedStudio.prefecture,
              data: analytics,
            },
          ]);
      console.log(analytics);
    } catch (error) {
      console.error("統計データの取得に失敗しました:", error);
    } finally {
      setIsMonthPaymentLoading(false);
    }
  };

  // 全スタジオのデータを各月ごとに合計
  const aggregateMonthlyData = (allStudiosData) => {
    const aggregatedData = [];

    // 月ごとのデータを初期化
    for (let i = 1; i <= 12; i++) {
      aggregatedData.push({
        month: i.toString(),
        members_count: 0,
        studio_cost: 0,
        trainfare: 0,
        advertising_cost: 0,
        plan_students: {
          week1_plan: 0,
          week2_plan: 0,
          week3_plan: 0,
          week4_plan: 0,
          week5_plan: 0,
          week6_plan: 0,
          other_plan: 0,
        },
        trials_count: 0,
      });
    }

    // 各店舗のデータを集計
    allStudiosData.forEach((studio) => {
      studio.data.forEach((monthData) => {
        const monthIndex = parseInt(monthData.month) - 1;
        aggregatedData[monthIndex].members_count += monthData.members_count;
        aggregatedData[monthIndex].studio_cost += monthData.studio_cost;
        aggregatedData[monthIndex].trainfare += monthData.trainfare;
        aggregatedData[monthIndex].advertising_cost +=
          monthData.advertising_cost;
        aggregatedData[monthIndex].trials_count += monthData.trials_count;

        Object.keys(monthData.plan_students).forEach((plan) => {
          aggregatedData[monthIndex].plan_students[plan] +=
            monthData.plan_students[plan];
        });
      });
    });

    return aggregatedData;
  };

  useEffect(() => {
    fetchMonthPayment();
  }, [selectedStudio, selectedYear]);

  useEffect(() => {
    setLocalAdvertisingCosts(
      monthPayment.map((payment) =>
        formatNumberWithCommas(payment.advertising_cost)
      )
    );
    setLocalStudioCosts(
      monthPayment.map((payment) => formatNumberWithCommas(payment.studio_cost))
    );
    setLocalTrainfares(
      monthPayment.map((payment) => formatNumberWithCommas(payment.trainfare))
    );
    setIsFocus(monthPayment.map(() => false));
  }, [monthPayment]);

  // スタジオリストの取得
  const fetchStudios = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStudios(data);
      setSelectedStudio(data["大阪"][0]);
    } catch (error) {
      console.error("スタジオデータの取得に失敗しました:", error);
    }
  };

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchTrades = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_all_trades/`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const trades = await response.json();
      setTrades(trades);
    } catch (error) {
      console.error("ログデータの取得に失敗しました:", error);
    }
  };

  // bodyに.analyticsを付与
  useEffect(() => {
    fetchTrades();
    document.body.classList.add("analyticsBody");
    return () => {
      document.body.classList.remove("analyticsBody");
    };
  }, []);

  //
  const aggregateData = (trades) => {
    const monthlyCategorySums = {};

    trades.forEach((trade) => {
      const month = trade.date.slice(0, 7); // YYYY-MM 形式で月を取得
      if (!monthlyCategorySums[month]) {
        monthlyCategorySums[month] = {};
      }
      const category = trade.category;
      if (!monthlyCategorySums[month][category]) {
        monthlyCategorySums[month][category] = 0;
      }
      monthlyCategorySums[month][category] += trade.amount;
    });

    return monthlyCategorySums;
  };

  const getCategoryList = (trades) => {
    const categorySet = new Set();
    trades.forEach((trade) => {
      categorySet.add(trade.category);
    });
    return Array.from(categorySet);
  };

  // グラフアニメーション
  useEffect(() => {
    const rows = document.querySelectorAll(".tradeCarts tbody tr");
    let index = 0;
    const interval = setInterval(() => {
      if (index < rows.length) {
        rows[index].classList.add("on");
        index++;
      } else {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [trades]); // 依存配列にtradesを追加して、tradesが更新されたときに再度実行

  // フォームの３桁コロン
  // 数値を3桁ごとにコンマで区切る関数
  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // カンマを削除する関数
  const removeCommas = (number) => {
    return number.toString().replace(/,/g, "");
  };

  // 入力値の変更をハンドル
  const handleInputChange = (index, field, value) => {
    switch (field) {
      case "studio_cost":
        const updatedStudioCosts = [...localStudioCosts];
        updatedStudioCosts[index] = value;
        setLocalStudioCosts(updatedStudioCosts);
        break;
      case "advertising_cost":
        const updatedAdvertisingCosts = [...localAdvertisingCosts];
        updatedAdvertisingCosts[index] = value;
        setLocalAdvertisingCosts(updatedAdvertisingCosts);
        break;
      case "trainfare":
        const updatedTrainfares = [...localTrainfares];
        updatedTrainfares[index] = value;
        setLocalTrainfares(updatedTrainfares);
        break;
      default:
        console.warn(`Unknown field: ${field}`);
    }
  };

  const handleFocus = (index, field) => {
    switch (field) {
      case "studio_cost":
        const updatedStudioCosts = [...localStudioCosts];
        updatedStudioCosts[index] = removeCommas(updatedStudioCosts[index]);
        setLocalStudioCosts(updatedStudioCosts);
        break;
      case "advertising_cost":
        const updatedAdvertisingCosts = [...localAdvertisingCosts];
        updatedAdvertisingCosts[index] = removeCommas(
          updatedAdvertisingCosts[index]
        );
        setLocalAdvertisingCosts(updatedAdvertisingCosts);
        break;
      case "trainfare":
        const updatedTrainfares = [...localTrainfares];
        updatedTrainfares[index] = removeCommas(updatedTrainfares[index]);
        setLocalTrainfares(updatedTrainfares);
        break;
      default:
        console.warn(`Unknown field: ${field}`);
    }

    const updatedIsFocus = [...isFocus];
    updatedIsFocus[index] = true;
    setIsFocus(updatedIsFocus);
  };

  const handleBlur = (index, field) => {
    const updatedMonthPayment = [...monthPayment];
    switch (field) {
      case "studio_cost":
        const updatedStudioCosts = [...localStudioCosts];
        const studioCostsValue = removeCommas(updatedStudioCosts[index]);
        updatedStudioCosts[index] = formatNumberWithCommas(studioCostsValue);
        setLocalStudioCosts(updatedStudioCosts);
        updatedMonthPayment[index].studio_cost = studioCostsValue;
        break;
      case "advertising_cost":
        const updatedAdvertisingCosts = [...localAdvertisingCosts];
        const advertisingCostsValue = removeCommas(
          updatedAdvertisingCosts[index]
        );
        updatedAdvertisingCosts[index] = formatNumberWithCommas(
          advertisingCostsValue
        );
        setLocalAdvertisingCosts(updatedAdvertisingCosts);
        updatedMonthPayment[index].advertising_cost = advertisingCostsValue;
        break;
      case "trainfare":
        const updatedTrainfares = [...localTrainfares];
        const trainfaresValue = removeCommas(updatedTrainfares[index]);
        updatedTrainfares[index] = formatNumberWithCommas(trainfaresValue);
        setLocalTrainfares(updatedTrainfares);
        updatedMonthPayment[index].trainfare = trainfaresValue;
        break;
      default:
        console.warn(`Unknown field: ${field}`);
    }

    setMonthPayment(updatedMonthPayment);

    const updatedIsFocus = [...isFocus];
    updatedIsFocus[index] = false;
    setIsFocus(updatedIsFocus);
  };

  // 更新ボタン押下時の処理
  const handleUpdate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_month_payment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studio_id: selectedStudio.id,
          year: selectedYear,
          month_payment: monthPayment,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("更新結果:", result);
      alert("更新しました");
    } catch (error) {
      console.error("データの更新に失敗しました:", error);
    }
  };

  // 全校更新ボタン押下時の処理
  const handleAllStudioReload = async () => {
    const confirmMessage =
      "全校の統計データを更新します。\n" +
      "処理に数分かかる場合があります。実行中は画面を閉じないでください。\n" +
      "※所属者数/プラン数は再計算で上書きされます。\n\n" +
      "続行しますか？";
    if (window.confirm(confirmMessage)) {
      setIsAllStudioUpdating(true);
      setUpdateProgress({
        done: 0,
        total: 0,
        percent: 0,
        year: "",
        studio_name: "",
        month: null,
      });

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/update_all_studio_reload/?stream=1`,
          {
            method: "GET",
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          const result = await response.json();
          console.log("更新結果:", result);
        } else {
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            lines.forEach((line) => {
              if (!line.trim()) return;
              try {
                const payload = JSON.parse(line);
                if (payload.type === "start") {
                  setUpdateProgress((prev) => ({
                    ...prev,
                    total: payload.total || prev.total,
                    percent: 0,
                    done: 0,
                  }));
                } else if (payload.type === "progress") {
                  setUpdateProgress({
                    done: payload.done || 0,
                    total: payload.total || 0,
                    percent: payload.percent || 0,
                    year: payload.year || "",
                    studio_name: payload.studio_name || "",
                    month: payload.month || null,
                  });
                } else if (payload.type === "done") {
                  setUpdateProgress((prev) => ({
                    ...prev,
                    percent: 100,
                    done: prev.total || payload.total || prev.done,
                  }));
                }
              } catch (e) {
                console.warn("進捗データの解析に失敗:", e);
              }
            });
          }
        }

        await fetchMonthPayment();
        alert("更新しました");
      } catch (error) {
        console.error("データの更新に失敗しました:", error);
        alert("更新に失敗しました");
      } finally {
        setIsAllStudioUpdating(false);
      }
    }
  };

  // プラスマイナス色付け
  const formatIntElements = document.querySelectorAll(".format-int");
  formatIntElements.forEach((element) => {
    const value = parseInt(element.textContent.replace(/,/g, ""));
    if (value > 0) {
      if (!element.textContent.includes("+")) {
        element.textContent = "+" + element.textContent;
      }
      // element.style.color = "blue";
    } else if (value < 0) {
      if (!element.textContent.includes("-")) {
        element.textContent = "-" + element.textContent;
      }
      element.style.color = "red";
    }
  });

  useEffect(() => {
    let selectedStudioIds = [];
    if (selectedStudio) {
      if (selectedStudio.id === 0) {
        // 全スタジオページ
        selectedStudioIds = selectedTrials;
      } else {
        // 各スタジオのページ
        selectedStudioIds.push(selectedStudio.id);
      }
    }
    fetchTrials(selectedStudioIds);
  }, [selectedStudio, selectedTrials, selectedYear]);

  // 体験者データの取得
  const fetchTrials = async (studioIds) => {
    setIsTrialsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_trials/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          year: selectedYear,
          studio_ids: studioIds,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const graphAry = data.map((x) => ({
        type: "line",
        label: `${x.studio_name}校 体験者数`,
        data: x.data.map((d) => d.trials_count),
        borderColor: "rgba(255, 255, 255, 0.8)",
        yAxisID: x.studio_id === 0 ? "allTrialLinery" : "trialLinery",
      }));
      setTrialsCount(graphAry);
    } catch (error) {
      console.error("体験者データの取得に失敗しました:", error);
    } finally {
      setIsTrialsLoading(false);
    }
  };

  // 更新ボタン押下時の処理
  const handleOldInsert = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/insert_old_data/`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("更新結果:", result);
      alert("インサートしました");
    } catch (error) {
      console.error("データの更新に失敗しました:", error);
    }
  };

  const progressPercent = Math.min(
    100,
    Math.max(0, updateProgress.percent || 0)
  );
  const progressDetail =
    updateProgress.year && updateProgress.studio_name && updateProgress.month
      ? `${updateProgress.year}年 ${updateProgress.studio_name} ${updateProgress.month}月`
      : "準備中...";
  const isGraphLoading = isMonthPaymentLoading || isTrialsLoading;

  return (
    <>
      <div className="analytics-content">
        <AnalyticsSidebar />
        {/* 売上配分 */}
        <div id="analytics" className="analytics" style={{ display: "none" }}>
          {/* グラフ */}
          <table className="tradeCarts charts-css column show-heading show-labels show-data-on-hover show-primary-axis show-10-secondary-axes show-data-axes data-spacing-6 show-3-secondary-axes multiple stacked">
            <caption> Simple Stacked Columns </caption>
            <thead>
              <tr>
                <th scope="col"> Continent </th>
                <th scope="col"> 売上 </th>
                <th scope="col"> 人件費(イントラ) </th>
                <th scope="col"> 人件費(社員) </th>
                <th scope="col"> レンタルスタジオ </th>
                <th scope="col"> 家賃 </th>
                <th scope="col"> 雑費 </th>
                <th scope="col"> 広告宣伝費 </th>
                <th scope="col"> 交通費 </th>
                <th scope="col"> 開発費 </th>
                <th scope="col"> 備品 </th>
                <th scope="col"> 衣装 </th>
                <th scope="col"> 交際費 </th>
                <th scope="col"> 福利厚生費 </th>
                <th scope="col"> 保険 </th>
                <th scope="col"> 手数料 </th>
                <th scope="col"> 光熱費 </th>
                <th scope="col"> 人件費 </th>
                <th scope="col"> 利益 </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(aggregateData(trades))
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([month, categories]) => {
                  return (
                    <tr key={month}>
                      <th scope="row">{month}</th>
                      {Object.entries(categories).map(
                        ([categoryName, categoryValue]) => (
                          <td
                            className={`data-${categoryName}`}
                            style={{ "--size": `${categoryValue / 20000000}` }}
                          >
                            <span className="data">{categoryValue || 0}</span>
                          </td>
                        )
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* データ */}
          <table className="dataList">
            <thead>
              <tr>
                {/*  */}
                <th></th>
                {getCategoryList(trades).map((category) => (
                  <th className="money_1">{category}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(aggregateData(trades))
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([yearMonth, categories]) => (
                  <tr key={yearMonth}>
                    <td dataDate={yearMonth}>{yearMonth}</td>
                    {getCategoryList(trades).map((category) => (
                      <td dataMoney="money_1">{categories[category] || 0}</td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* 各スタジオごと */}
        <div id="studioData">
          {/* スタジオサイドバー */}
          <ul className="studioList">
            {/* ・旧のデータをDBに登録したい際は下記ボタンのコメントアウトを外しボタンを表示
            ・djangoのviews.pyのinsert_old_data_viewのcsv_dataを調整して使う */}
            {/* <button className="allStudioReload" onClick={handleOldInsert}>
              旧データインサート
            </button> */}
            <button className="allStudioReload" onClick={handleAllStudioReload}>
              更新
            </button>
            <button
              className="allstudio"
              onClick={() => {
                setSelectedStudio({ id: 0, name: "全スタジオ" });
              }}
            >
              全スタジオ
            </button>
            {Object.entries(studios).map(([prefecture, studioList]) => (
              <React.Fragment key={prefecture}>
                <p>{prefecture}</p>
                <ul>
                  {studioList.map((studio) => (
                    <li
                      key={studio.id}
                      className={
                        selectedStudio?.name === studio.name ? "active" : ""
                      }
                      onClick={() => {
                        setSelectedStudio(studio);
                      }}
                    >
                      {studio.name}校
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            ))}
          </ul>

          {/* 各スタジオ */}
          <div
            id="single_StudioData"
            className="studioContent"
            style={{
              display:
                selectedStudio && selectedStudio.id !== 0 ? "block" : "none",
            }}
          >
            <div className="dateWrap">
              <h2>{selectedStudio?.name}校</h2>
              {/* <div className="dateSelect">
                <div className="year">
                  <select
                    id="locationSelect"
                    name="location"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="2024">2024年</option>
                    <option value="2023">2023年</option>
                    <option value="2022">2022年</option>
                  </select>
                </div>
              </div> */}
            </div>
            <StudioData
              monthPayment={monthPayment}
              revenues={revenues}
              salaries={salaries}
              trialsCount={trialsCount}
              loading={isGraphLoading}
            />

            <nav id="singleStudioPage" className="tabChangebtn">
              <ul>
                {years.map((year) => (
                  <li
                    key={year}
                    className={selectedYear === String(year) ? "on" : ""}
                    onClick={() => setSelectedYear(String(year))}
                  >
                    {year}
                  </li>
                ))}
              </ul>
            </nav>

            <div className="dataGraph">
              {/*  */}
              <div className="data-grid">
                <p className="label"></p>
                <p
                  className={`label plan-detail-btn${
                    isMembersCountOpen ? " on" : ""
                  }`}
                  onClick={() => {
                    setIsMembersCountOpen(!isMembersCountOpen);
                  }}
                >
                  <FontAwesomeIcon icon={faTriangle} />
                  所属者数
                </p>
                <p
                  className="label toggle-column plan-detail"
                  style={{ display: isMembersCountOpen ? "grid" : "none" }}
                >
                  所属者詳細
                </p>
                <p className="label">体験者数</p>
                <p
                  className={`label uriage-detail-btn${
                    isRevenueOpen ? " on" : ""
                  }`}
                  onClick={() => {
                    setIsRevenueOpen(!isRevenueOpen);
                  }}
                >
                  <FontAwesomeIcon icon={faTriangle} />
                  売上
                </p>
                <p
                  className="label toggle-column uriage-detail"
                  style={{ display: isRevenueOpen ? "grid" : "none" }}
                >
                  売上詳細
                </p>
                <p className="label">スタジオ代</p>
                <p className="label">給料</p>
                <p className="label">交通費</p>
                <p className="label">広告費</p>
                <p className="label">合計</p>
              </div>
              {/*  */}
              {monthPayment.map((payment, index) => (
                <div className="data-grid month" key={index}>
                  <p>{index + 1}月</p>
                  {/* 所属者数 */}
                  <div className="plan">
                    <input
                      type="text"
                      name="members_count"
                      inputmode="numeric"
                      pattern="\d*"
                      value={payment.members_count}
                      onFocus={(e) =>
                        (e.target.value = removeCommas(e.target.value))
                      }
                      onBlur={(e) =>
                        (e.target.value = formatNumberWithCommas(
                          e.target.value
                        ))
                      }
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "members_count",
                          e.target.value
                        )
                      }
                    />

                    <dl
                      className="details toggle-column plan-detail"
                      style={{
                        display: isMembersCountOpen ? "grid" : "none",
                      }}
                    >
                      {Object.entries(payment.plan_students).map(
                        ([plan, count]) => (
                          <>
                            <dt>
                              {plan === "other_plan"
                                ? "ポイント"
                                : plan
                                    .replace("week", "週")
                                    .replace("_plan", "")}
                            </dt>
                            <dd>{count}人</dd>
                          </>
                        )
                      )}
                    </dl>
                  </div>
                  {/* 体験者数 */}
                  <input
                    type="text"
                    name="trials_count"
                    inputMode="numeric"
                    pattern="\d*"
                    value={payment.trials_count}
                    onClick={() => {
                      console.log(monthPayment);
                    }}
                    onFocus={() => handleFocus(index, "trials_count")}
                    onBlur={() => handleBlur(index, "trials_count")}
                    onChange={(e) =>
                      handleInputChange(index, "trials_count", e.target.value)
                    }
                  />
                  {/* ①売上 */}
                  <div className="uriage">
                    {/* ①売上 */}
                    <input
                      type="text"
                      name="revenue"
                      inputMode="numeric"
                      pattern="\d*"
                      value={formatNumberWithCommas(
                        revenues.find((rev) => rev.month === index + 1)
                          ?.revenue || ""
                      )}
                      disabled
                    />

                    <dl
                      className="details toggle-column uriage-detail"
                      style={{ display: isRevenueOpen ? "grid" : "none" }}
                    >
                      {Object.entries(payment.plan_students).map(
                        ([plan, count]) => {
                          const planRevenue =
                            plan === "other_plan"
                              ? planPrices["other_plan"] * count
                              : (planPrices[plan] /
                                  parseInt(
                                    plan
                                      .replace("week", "")
                                      .replace("_plan", "")
                                  )) *
                                count;
                          return (
                            <>
                              <dt>
                                {plan === "other_plan"
                                  ? "ポイント"
                                  : plan
                                      .replace("week", "週")
                                      .replace("_plan", "")}
                              </dt>
                              <dd>{planRevenue}円</dd>
                            </>
                          );
                        }
                      )}
                    </dl>
                  </div>
                  {/* ②家賃 */}
                  <input
                    type="text"
                    name="studio_cost"
                    inputMode="numeric"
                    pattern="\d*"
                    value={localStudioCosts[index] || ""}
                    onFocus={() => handleFocus(index, "studio_cost")}
                    onBlur={() => handleBlur(index, "studio_cost")}
                    onChange={(e) =>
                      handleInputChange(index, "studio_cost", e.target.value)
                    }
                  />
                  {/* ③給料 */}
                  <input
                    type="text"
                    name="salary"
                    inputMode="numeric"
                    pattern="\d*"
                    value={formatNumberWithCommas(
                      salaries.find((sal) => sal.month === index + 1)?.salary ||
                        ""
                    )}
                    disabled
                  />
                  {/* ④交通費 */}
                  <input
                    type="text"
                    name="trainfare"
                    inputMode="numeric"
                    pattern="\d*"
                    value={localTrainfares[index] || ""}
                    onFocus={() => handleFocus(index, "trainfare")}
                    onBlur={() => handleBlur(index, "trainfare")}
                    onChange={(e) =>
                      handleInputChange(index, "trainfare", e.target.value)
                    }
                  />
                  {/* ⑤広告費 */}
                  <input
                    type="text"
                    name="advertising_cost"
                    inputMode="numeric"
                    pattern="\d*"
                    value={localAdvertisingCosts[index] || ""}
                    onFocus={() => handleFocus(index, "advertising_cost")}
                    onBlur={() => handleBlur(index, "advertising_cost")}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        "advertising_cost",
                        e.target.value
                      )
                    }
                  />
                  {/* ⑥合計 */}
                  <p>
                    {formatNumberWithCommas(
                      revenues.find((rev) => rev.month === index + 1)?.revenue -
                        payment.studio_cost -
                        salaries.find((sal) => sal.month === index + 1)
                          ?.salary -
                        payment.trainfare -
                        payment.advertising_cost
                    )}
                    円
                  </p>
                </div>
              ))}
            </div>
            <div className="btn-wrap">
              <button className="submit" type="submit" onClick={handleUpdate}>
                この内容で更新
              </button>
            </div>
          </div>

          {/* 全校合計データ */}
          <div className="">
            <div
              id="all_StudioData"
              className="studioContent"
              style={{
                display:
                  selectedStudio && selectedStudio.id === 0 ? "block" : "none",
              }}
            >
              <div className="dateWrap">
                <h2>全スタジオ合計データ</h2>
                <div className="dateSelect">
                  <div className="year">
                    <select
                      id="locationSelect"
                      name="location"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      {years.map((year) => (
                        <option key={year} value={String(year)}>
                          {year}年
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="newvisitor-sort">
                <p>体験者数</p>
                <ul>
                  <li
                    onClick={() => {
                      let trialsAry = [...selectedTrials];
                      const index = trialsAry.indexOf(0);
                      if (index > -1) {
                        trialsAry = [];
                      } else {
                        trialsAry = [0];
                      }
                      setSelectedTrials(trialsAry);
                    }}
                    className={selectedTrials.includes(0) ? "active" : ""}
                  >
                    全校合計
                  </li>
                  {Object.entries(studios).map(([prefecture, studioList]) => (
                    <React.Fragment key={prefecture}>
                      {studioList.map((studio) => (
                        <li
                          key={studio.id}
                          className={
                            selectedTrials.includes(studio.id) ? "active" : ""
                          }
                          onClick={() => {
                            let trialsAry = [...selectedTrials];
                            const index = trialsAry.indexOf(studio.id);
                            if (index > -1) {
                              trialsAry.splice(index, 1);
                            } else {
                              trialsAry.push(studio.id);
                            }
                            const zeroIndex = trialsAry.indexOf(0);
                            if (zeroIndex > -1) {
                              trialsAry.splice(zeroIndex, 1);
                            }
                            setSelectedTrials(trialsAry);
                          }}
                        >
                          {studio.name}校
                        </li>
                      ))}
                    </React.Fragment>
                  ))}
                </ul>
              </div>

              <StudioData
                monthPayment={monthPayment}
                revenues={revenues}
                salaries={salaries}
                trialsCount={trialsCount}
                loading={isGraphLoading}
              />

              {/* タブ切り替え */}
              <nav id="allStudioPage" className="tabChangebtn">
                <ul>
                  <li
                    className={isSelectedTab === 0 ? "on" : ""}
                    onClick={() => {
                      setIsSelectedTab(0);
                    }}
                  >
                    全スタジオ合計
                  </li>
                  <li
                    className={isSelectedTab === 1 ? "on" : ""}
                    onClick={() => {
                      setIsSelectedTab(1);
                    }}
                  >
                    スタジオ別比較
                  </li>
                </ul>
              </nav>

              {/*  */}
              <div
                id="total_Graph"
                className="dataGraph"
                style={{ display: isSelectedTab === 0 ? "grid" : "none" }}
              >
                {/*  */}
                <div className="data-grid">
                  <p className="label"></p>
                  <p
                    className={`label plan-detail-btn${
                      isMembersCountOpen ? " on" : ""
                    }`}
                    onClick={() => {
                      setIsMembersCountOpen(!isMembersCountOpen);
                    }}
                  >
                    <FontAwesomeIcon icon={faTriangle} />
                    所属者数
                  </p>
                  <p
                    className="label toggle-column plan-detail"
                    style={{ display: isMembersCountOpen ? "grid" : "none" }}
                  >
                    所属者詳細
                  </p>
                  <p className="label">体験者数</p>
                  <p
                    className={`label uriage-detail-btn${
                      isRevenueOpen ? " on" : ""
                    }`}
                    onClick={() => {
                      setIsRevenueOpen(!isRevenueOpen);
                    }}
                  >
                    <FontAwesomeIcon icon={faTriangle} />
                    売上
                  </p>
                  <p
                    className="label toggle-column uriage-detail"
                    style={{ display: isRevenueOpen ? "grid" : "none" }}
                  >
                    売上詳細
                  </p>
                  <p className="label">スタジオ代</p>
                  <p className="label">給料</p>
                  <p className="label">交通費</p>
                  <p className="label">広告費</p>
                  <p className="label">合計</p>
                </div>
                {/*  */}
                {monthPayment.map((payment, index) => (
                  <div className="data-grid month" key={index}>
                    <p>{index + 1}月</p>
                    {/* 所属者数 */}
                    <div className="plan">
                      <input
                        type="text"
                        name="members_count"
                        inputmode="numeric"
                        pattern="\d*"
                        value={payment.members_count}
                        onFocus={(e) =>
                          (e.target.value = removeCommas(e.target.value))
                        }
                        onBlur={(e) =>
                          (e.target.value = formatNumberWithCommas(
                            e.target.value
                          ))
                        }
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "members_count",
                            e.target.value
                          )
                        }
                      />

                      <dl
                        className="details toggle-column plan-detail"
                        style={{
                          display: isMembersCountOpen ? "grid" : "none",
                        }}
                      >
                        {Object.entries(payment.plan_students).map(
                          ([plan, count]) => (
                            <>
                              <dt>
                                {plan === "other_plan"
                                  ? "ポイント"
                                  : plan
                                      .replace("week", "週")
                                      .replace("_plan", "")}
                              </dt>
                              <dd>{count}人</dd>
                            </>
                          )
                        )}
                      </dl>
                    </div>
                    {/* 体験者数 */}
                    <input
                      type="text"
                      name="trials_count"
                      inputMode="numeric"
                      pattern="\d*"
                      value={payment.trials_count}
                      onClick={() => {
                        console.log(monthPayment);
                      }}
                      onFocus={() => handleFocus(index, "trials_count")}
                      onBlur={() => handleBlur(index, "trials_count")}
                      onChange={(e) =>
                        handleInputChange(index, "trials_count", e.target.value)
                      }
                    />
                    {/* ①売上 */}
                    <div className="uriage">
                      {/* ①売上 */}
                      <input
                        type="text"
                        name="revenue"
                        inputMode="numeric"
                        pattern="\d*"
                        value={formatNumberWithCommas(
                          revenues.find((rev) => rev.month === index + 1)
                            ?.revenue || ""
                        )}
                        disabled
                      />

                      <dl
                        className="details toggle-column uriage-detail"
                        style={{ display: isRevenueOpen ? "grid" : "none" }}
                      >
                        {Object.entries(payment.plan_students).map(
                          ([plan, count]) => {
                            const planRevenue =
                              plan === "other_plan"
                                ? planPrices["other_plan"] * count
                                : (planPrices[plan] /
                                    parseInt(
                                      plan
                                        .replace("week", "")
                                        .replace("_plan", "")
                                    )) *
                                  count;
                            return (
                              <>
                                <dt>
                                  {plan === "other_plan"
                                    ? "ポイント"
                                    : plan
                                        .replace("week", "週")
                                        .replace("_plan", "")}
                                </dt>
                                <dd>{planRevenue}円</dd>
                              </>
                            );
                          }
                        )}
                      </dl>
                    </div>
                    {/* ②家賃 */}
                    <input
                      type="text"
                      name="studio_cost"
                      inputMode="numeric"
                      pattern="\d*"
                      value={localStudioCosts[index] || ""}
                      onFocus={() => handleFocus(index, "studio_cost")}
                      onBlur={() => handleBlur(index, "studio_cost")}
                      onChange={(e) =>
                        handleInputChange(index, "studio_cost", e.target.value)
                      }
                    />
                    {/* ③給料 */}
                    <input
                      type="text"
                      name="salary"
                      inputMode="numeric"
                      pattern="\d*"
                      value={formatNumberWithCommas(
                        salaries.find((sal) => sal.month === index + 1)
                          ?.salary || ""
                      )}
                      disabled
                    />
                    {/* ④交通費 */}
                    <input
                      type="text"
                      name="trainfare"
                      inputMode="numeric"
                      pattern="\d*"
                      value={localTrainfares[index] || ""}
                      onFocus={() => handleFocus(index, "trainfare")}
                      onBlur={() => handleBlur(index, "trainfare")}
                      onChange={(e) =>
                        handleInputChange(index, "trainfare", e.target.value)
                      }
                    />
                    {/* ⑤広告費 */}
                    <input
                      type="text"
                      name="advertising_cost"
                      inputMode="numeric"
                      pattern="\d*"
                      value={localAdvertisingCosts[index] || ""}
                      onFocus={() => handleFocus(index, "advertising_cost")}
                      onBlur={() => handleBlur(index, "advertising_cost")}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "advertising_cost",
                          e.target.value
                        )
                      }
                    />
                    {/* ⑥合計 */}
                    <p>
                      {formatNumberWithCommas(
                        revenues.find((rev) => rev.month === index + 1)
                          ?.revenue -
                          payment.studio_cost -
                          salaries.find((sal) => sal.month === index + 1)
                            ?.salary -
                          payment.trainfare -
                          payment.advertising_cost
                      )}
                      円
                    </p>
                  </div>
                ))}
              </div>

              {/*  */}
              <div
                id="comparison_Graph"
                className="dataGraph"
                style={{ display: isSelectedTab === 1 ? "grid" : "none" }}
              >
                {/*  */}
                <div className="data-grid">
                  <p></p>
                  <p></p>
                  {selectedStudio && selectedStudio.id === 0 && (
                    <>
                      {studioAnalyticsData.map((studio, index) => (
                        <p key={index} className="label h-keep">
                          {studio.studio_name}
                          {" - "}
                          <span>{studio.prefecture}</span>
                        </p>
                      ))}
                    </>
                  )}
                </div>
                {/*  */}
                {Array.from({ length: 12 }, (_, i) => {
                  return (
                    <div className="data-grid all-data" key={i}>
                      <p className="monthText">{i + 1}月</p>
                      {/*  */}
                      <div className="row ttl">
                        <p>売上(千円)</p>
                        <p>-</p>
                        <p>経費(千円)</p>
                        <p>利益</p>
                      </div>
                      {selectedStudio && selectedStudio.id === 0 && (
                        <>
                          {studioAnalyticsData.map((studio, index) => {
                            // 所属生徒
                            const plan_students = studio.data.find(
                              (item) => item.month == (i + 1).toString()
                            )?.plan_students;
                            // 売上
                            const totalRevenue = Object.entries(
                              plan_students
                            ).reduce((total, [plan, count]) => {
                              const planRevenue =
                                plan === "other_plan"
                                  ? planPrices["other_plan"] * count
                                  : (planPrices[plan] /
                                      parseInt(
                                        plan
                                          .replace("week", "")
                                          .replace("_plan", "")
                                      )) *
                                    count;
                              return total + planRevenue;
                            }, 0);
                            // 給料
                            const totalSalary = Object.entries(
                              plan_students
                            ).reduce((total, [plan, count]) => {
                              let planSalary;
                              if (
                                plan === "other_plan" ||
                                plan === "week1_plan"
                              ) {
                                planSalary = 1360 * count;
                              } else if (plan === "week2_plan") {
                                planSalary = 1000 * count;
                              } else {
                                planSalary = 900 * count;
                              }
                              return total + planSalary;
                            }, 0);
                            // 経費
                            const totalCost =
                              studio.data.find(
                                (item) => item.month == (i + 1).toString()
                              )?.studio_cost +
                              totalSalary +
                              studio.data.find(
                                (item) => item.month == (i + 1).toString()
                              )?.trainfare +
                              studio.data.find(
                                (item) => item.month == (i + 1).toString()
                              )?.advertising_cost;
                            return (
                              <div className="row">
                                <p>{Math.floor(totalRevenue / 1000)}</p>
                                <p>-</p>
                                <p>{Math.floor(totalCost / 1000)}</p>
                                <p className="format-int">
                                  {totalRevenue - totalCost}
                                </p>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="loading"
        style={{ display: isAllStudioUpdating ? "flex" : "none" }}
      >
        <div className="loading-content">
          <p>書き込み中... {progressPercent}%</p>
          <div className="progress-bar">
            <span style={{ width: `${progressPercent}%` }}></span>
          </div>
          <p className="loading-detail">{progressDetail}</p>
        </div>
      </div>
    </>
  );
}

export default AnalyticsPage;
